import { useEffect, useState } from "react";
import { longDate } from "@/components/landing/SlotPicker";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, CalendarCheck, Phone, Mail, BellRing } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-2xl border border-[#1B3B28]/15 bg-[#FCFBF7] px-4 py-3.5 text-[#1B3B28] focus:outline-none focus:ring-2 focus:ring-[#86C89B]";
const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const CHECKIN_LABELS = { bene: "Sto bene", fastidio: "Un po' di fastidio", aiuto: "Ho bisogno di aiuto" };

const PatientCard = ({ b }) => (
  <div className="rounded-[1.75rem] bg-[#E6ECE4] p-7" data-testid="appointment-patient-card">
    <p className="eyebrow text-[#3E6B48]">Paziente · <span data-testid="appointment-treatment">{b.trattamento === "igiene_gbt" ? "Igiene GBT" : "Visita parodontale"}</span></p>
    <p className="f-serif text-4xl text-[#1B3B28] mt-2" data-testid="appointment-patient-name">{b.nome} {b.cognome}</p>
    <div className="mt-5 space-y-2 text-sm text-[#1B3B28]">
      <a href={`tel:${b.telefono}`} className="flex items-center gap-2" data-testid="appointment-patient-phone"><Phone size={14} /> {b.telefono}</a>
      <a href={`mailto:${b.email}`} className="flex items-center gap-2 break-all"><Mail size={14} /> {b.email}</a>
    </div>
    <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
      <div><dt className="text-[#526356]">Preferenza</dt><dd className="text-[#1B3B28]">{b.preferenza}</dd></div>
      <div><dt className="text-[#526356]">Ansia</dt><dd className="text-[#1B3B28]" data-testid="appointment-patient-anxiety">{b.ansia}/10</dd></div>
      <div className="col-span-2"><dt className="text-[#526356]">Sintomi</dt><dd className="text-[#1B3B28]">{b.sintomi.join(", ") || "Nessuno indicato"}</dd></div>
      {b.note && <div className="col-span-2"><dt className="text-[#526356]">Note</dt><dd className="text-[#1B3B28]">{b.note}</dd></div>}
    </dl>
  </div>
);

const Status = ({ b }) => {
  if (!b.appointment_date) return null;
  const d = new Date(`${b.appointment_date}T00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#86C89B]/60 bg-[#FCFBF7] p-4 text-sm text-[#1B3B28]" data-testid="appointment-status">
      <BellRing size={16} className="mt-0.5 text-[#3E6B48]" />
      <p>Visita fissata <b>{d}</b> alle <b>{b.appointment_time}</b>. {b.reminder_sent ? "Promemoria già inviato al paziente." : "Il paziente riceverà il promemoria con l'esercizio di respiro il giorno prima."}
        {(b.laser_session || b.trattamento === "igiene_gbt") && <span className="block mt-1 text-[#3E6B48]" data-testid="appointment-aftercare-note">{b.aftercare_sent ? "Consigli post-seduta già inviati." : `Consigli post-${b.laser_session ? "seduta laser" : "igiene GBT"} programmati per il giorno dopo.`}</span>}
        {b.laser_session && (
          <span className="block mt-1 text-[#3E6B48]" data-testid="appointment-checkin-note">
            {b.checkin_response ? <>Check-in del paziente: <b>{CHECKIN_LABELS[b.checkin_response]}</b>{b.checkin_note && ` — “${b.checkin_note}”`}</> : b.checkin_sent ? "Check-in inviato, in attesa di risposta." : "Check-in \"Come ti senti?\" programmato 3 giorni dopo."}
          </span>
        )}
        {b.confirmation_sent === true && <span className="block mt-1 text-[#3E6B48]" data-testid="appointment-confirmation-sent">Email di conferma con giorno e ora inviata al paziente.</span>}
        {b.confirmation_sent === false && <span className="block mt-1 text-[#9A4B3C]" data-testid="appointment-confirmation-failed">Non è stato possibile inviare l'email di conferma: avvisa il paziente su WhatsApp.</span>}
      </p>
    </div>
  );
};

export default function AppointmentPage() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ appointment_date: "", appointment_time: "", laser_session: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/appointments/${token}`)
      .then(({ data }) => { setB(data); setForm({ appointment_date: data.appointment_date || data.requested_date || "", appointment_time: data.appointment_time || data.requested_time || "", laser_session: data.laser_session }); })
      .catch(() => setError(true));
  }, [token]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/appointments/${token}`, form);
      setB(data);
      toast.success(data.confirmation_sent ? "Visita salvata. Conferma inviata al paziente." : "Visita salvata, ma l'email di conferma non è partita.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Controlla data e ora.");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F3EC] px-6 py-10 sm:py-16" data-testid="appointment-page">
      <div className="mx-auto max-w-4xl">
        <Logo />
        {error && <p className="mt-16 f-serif text-4xl text-[#1B3B28]" data-testid="appointment-not-found">Link non valido o richiesta non trovata.</p>}
        {!b && !error && <Loader2 className="mt-16 animate-spin text-[#3E6B48]" />}
        {b && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-12 grid md:grid-cols-2 gap-8">
            <PatientCard b={b} />
            <form onSubmit={save} className="rounded-[1.75rem] bg-[#FCFBF7] border border-[#1B3B28]/10 p-7" data-testid="appointment-form">
              <p className="eyebrow text-[#3E6B48]">Fissa la visita</p>
              <h1 className="f-serif text-4xl text-[#1B3B28] mt-2 leading-none">Quando vi vedete?</h1>
              {b.requested_date && !b.appointment_date && (
                <p className="mt-5 rounded-2xl bg-[#F3E6D8] text-[#5A3B1A] px-4 py-3 text-sm" data-testid="appointment-requested-slot">
                  Il paziente ha scelto <b className="capitalize">{longDate(b.requested_date)} alle {b.requested_time}</b>: l'orario è già inserito qui sotto. Salva per confermarlo.
                </p>
              )}
              <label className="block mt-6 text-sm text-[#526356]">Data
                <input type="date" required min={today()} value={form.appointment_date} onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} className={`${inputCls} mt-2`} data-testid="appointment-date-input" />
              </label>
              <label className="block mt-4 text-sm text-[#526356]">Ora
                <input type="time" required step={900} value={form.appointment_time} onChange={(e) => setForm({ ...form, appointment_time: e.target.value })} className={`${inputCls} mt-2`} data-testid="appointment-time-input" />
              </label>
              <label className="mt-5 flex items-start gap-3 rounded-2xl bg-[#E6ECE4] p-4 cursor-pointer">
                <Checkbox checked={form.laser_session} onCheckedChange={(v) => setForm({ ...form, laser_session: !!v })} className="mt-0.5" data-testid="appointment-laser-checkbox" />
                <span className="text-sm text-[#1B3B28]">
                  <b>È la seduta di terapia laser</b>
                  <span className="block text-[#526356] mt-0.5">Il giorno dopo il paziente riceverà i consigli su cosa mangiare e come lavarsi i denti.</span>
                </span>
              </label>
              <button type="submit" disabled={saving} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] py-4 hover:bg-[#3E6B48] transition-colors disabled:opacity-60" data-testid="appointment-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} Salva e programma il promemoria
              </button>
              <Status b={b} />
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
